import classnames from 'classnames';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';

import { Image } from '@/domains/core/Image/Image';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import type { MediaType } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import { useBrand } from '@/domains/productDiscovery/ProductPage/modules/Brand/useBrand';
import { debounce } from '@/domains/productDiscovery/ProductPage/utils/debounce';

import { Controller } from './components/Controller/Controller';
import { Slide } from './components/Slide/Slide';
import { trackClickOnZoomModal } from './tracking';

import styles from './Carousel.module.scss';

type CarouselProps = React.PropsWithChildren<{
  className?: string;
}>;

const DynamicZoomModal = dynamic(
  () => import('./components/ZoomModal/ZoomModal').then((mod) => mod.ZoomModal),
  { ssr: false },
);

export const Carousel = ({ children, className }: CarouselProps) => {
  const { title, media, modelId } = useDetail();
  const brand = useBrand();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isZoomModalVisible, setIsZoomModalVisible] = useState(false);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const onScroll = debounce(() => {
    if (wrapperRef?.current) {
      const itemsDiv = wrapperRef.current as HTMLDivElement;
      const newIndex = Math.round(
        itemsDiv.scrollLeft / itemsDiv.getBoundingClientRect().width,
      );
      if (newIndex !== currentSlide) {
        setCurrentSlide(newIndex);
      }
    }
  }, 16);
  useEffect(() => {
    wrapperRef?.current?.scrollTo({
      left: currentSlide * wrapperRef?.current?.getBoundingClientRect().width,
      behavior: 'auto',
    });
  }, [currentSlide]);

  const onOpenZoomModal = () => {
    setIsZoomModalVisible(true);
    trackClickOnZoomModal(title);
  };

  // Reset the carousel index when switching model
  useEffect(() => {
    setCurrentSlide(0);
  }, [modelId]);

  const isImage = (type: MediaType) => type === 'image';

  return (
    <div
      className={classnames(styles.container, className)}
      data-testid="carousel"
    >
      <div className={styles.slider}>
        <div
          className={styles.wrapper}
          ref={wrapperRef}
          onScroll={onScroll}
          onClick={onOpenZoomModal}
          onKeyPress={onOpenZoomModal}
          role="button"
          tabIndex={0}
        >
          {media.map((mediaItem, index) => (
            <Slide
              mediaURL={mediaItem.regularUrl || ''}
              type={mediaItem.type}
              regularUrl={mediaItem.regularUrl || ''}
              zoomUrl={mediaItem.largeUrl || ''}
              alt={mediaItem.title || ''}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : 'auto'}
              key={mediaItem.regularUrl}
              isVisible={currentSlide === index}
            />
          ))}
        </div>
        <Controller
          currentSlide={currentSlide}
          numberOfImages={media.filter((item) => isImage(item.type)).length}
          numberOfVideos={media.filter((item) => !isImage(item.type)).length}
        />
        {brand.imageUrl && (
          <div className={styles.brand} data-testid="carousel-brand">
            <Image
              alt={brand.name || ''}
              src={brand.imageUrl}
              loading="lazy"
              height="24"
              width="141"
            />
          </div>
        )}
      </div>
      {isZoomModalVisible && (
        <DynamicZoomModal
          onClose={() => setIsZoomModalVisible(false)}
          currentSlide={currentSlide}
          onChangeSlide={setCurrentSlide}
        />
      )}
    </div>
  );
};

Carousel.displayName = 'Carousel';
